import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, useScroll, useTransform } from "framer-motion";
import { Lock, LogOut, Loader2, Plus, Minus, ShoppingBag, Check, Gift, ArrowRight, Tag, X } from "lucide-react";
import { useLenis } from "@/hooks/useLenis";
import { CONTACT } from "@/data/content";
import { PORTAL_TOKEN } from "@/data/uniforms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const SE_SHIP = ["GA","AL","SC","NC","TN","MS"];
const shipFeeFor = (st) => !st ? null : (st === "FL" ? 8 : SE_SHIP.includes(st) ? 12 : 15);

/* ---------------- Auth Gate ---------------- */
function AuthGate({ onAuthed }) {
  const [mode, setMode] = useState("register");
  const [f, setF] = useState({ name: "", email: "", password: "", access_code: "" });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let data;
      if (mode === "register") {
        ({ data } = await axios.post(`${API}/employee/register`, f));
      } else {
        ({ data } = await axios.post(`${API}/auth/login`, { email: f.email, password: f.password }));
      }
      localStorage.setItem(PORTAL_TOKEN, data.access_token);
      onAuthed();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-white/5 border border-white/15 rounded-sm px-4 py-3 text-white placeholder:text-white/30 focus:border-[#e0574d] outline-none transition-colors";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0e17] px-6 relative overflow-hidden">
      <div className="absolute inset-0 grain-overlay opacity-[0.06]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="text-xs uppercase tracking-[0.3em] text-white/40 hover:text-white/70">← Back to site</Link>
        <p className="mt-6 text-xs font-bold uppercase tracking-[0.3em] text-[#e0574d]">Employee Portal</p>
        <h1 className="mt-2 text-4xl font-extrabold text-white font-display leading-tight">Uniform Store</h1>
        <p className="mt-3 text-sm text-white/50">{mode === "register" ? "Create your account with the company access code." : "Welcome back — sign in to order."}</p>

        <form onSubmit={submit} data-testid="portal-auth-form" className="mt-8 space-y-4">
          {mode === "register" && (
            <input data-testid="portal-name" value={f.name} onChange={up("name")} placeholder="Full name" className={inp} required />
          )}
          <input data-testid="portal-email" type="email" value={f.email} onChange={up("email")} placeholder="Email" className={inp} required />
          <input data-testid="portal-password" type="password" value={f.password} onChange={up("password")} placeholder="Password" className={inp} required />
          {mode === "register" && (
            <input data-testid="portal-access-code" value={f.access_code} onChange={up("access_code")} placeholder="Company access code" className={inp} required />
          )}
          <button type="submit" disabled={loading} data-testid="portal-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#e0574d] text-white font-bold py-3.5 rounded-sm hover:bg-[#c8483f] transition-colors disabled:opacity-70">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={18} />}
            {mode === "register" ? "Create Account" : "Sign In"}
          </button>
        </form>
        <button onClick={() => setMode(mode === "register" ? "login" : "register")} className="mt-5 text-sm text-white/50 hover:text-white">
          {mode === "register" ? "Already have an account? Sign in" : "Need an account? Register"}
        </button>
      </motion.div>
    </div>
  );
}

/* ---------------- Marquee ---------------- */
const Marquee = () => (
  <div className="overflow-hidden border-y border-white/10 py-4 bg-[#0b0e17]">
    <motion.div
      className="flex whitespace-nowrap gap-8"
      animate={{ x: ["0%", "-50%"] }}
      transition={{ repeat: Infinity, ease: "linear", duration: 22 }}
    >
      {Array.from({ length: 2 }).map((_, i) => (
        <span key={i} className="flex gap-8 text-2xl font-extrabold font-display uppercase tracking-tight text-white/15">
          {["Uniforms", "CityWide", "Elite Commercial", "Vintage White", "First 2 Free", "Represent"].map((w) => (
            <span key={w} className="flex items-center gap-8">{w} <span className="text-[#e0574d]">✦</span></span>
          ))}
        </span>
      ))}
    </motion.div>
  </div>
);

/* ---------------- Shop ---------------- */
const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

function Shop({ onLogout }) {
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [sizes, setSizes] = useState({});
  const [promo, setPromo] = useState("");
  const [delivery, setDelivery] = useState("pickup");
  const [shipState, setShipState] = useState("");
  const [placing, setPlacing] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem(PORTAL_TOKEN)}` } });

  const loadMe = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/employee/me`, authHeader()); setMe(data); }
    catch { onLogout(); }
  }, [onLogout]);

  useEffect(() => { loadMe(); }, [loadMe]);

  useEffect(() => {
    axios.get(`${API}/uniform/products`)
      .then(({ data }) => setProducts(data))
      .catch(() => toast.error("Could not load uniforms."));
  }, []);

  const shirtPrice = products[0]?.price ?? 18;

  const addToCart = (product) => {
    const size = sizes[product.id];
    if (!size) { toast.error("Please select a size first."); return; }
    setCart((c) => {
      const idx = c.findIndex((i) => i.id === product.id && i.size === size);
      if (idx >= 0) { const n = [...c]; n[idx].qty += 1; return n; }
      return [...c, { id: product.id, brand: product.brand, color: product.color, size, qty: 1, price: product.price, image: product.image }];
    });
    setCartOpen(true);
    toast.success(`${product.brand} (${size}) added`);
  };

  const setQty = (i, delta) => setCart((c) => {
    const n = [...c]; n[i].qty += delta;
    if (n[i].qty <= 0) n.splice(i, 1);
    return n;
  });

  const totalQty = cart.reduce((s, i) => s + i.qty, 0);
  const freeRemaining = me?.free_remaining ?? 0;
  const shippingFee = delivery === "ship" ? shipFeeFor(shipState) : 0;
  const payableQty = Math.max(0, totalQty - freeRemaining);
  const itemsCost = payableQty * shirtPrice;
  const orderTotal = itemsCost + (shippingFee || 0);

  const checkout = async () => {
    if (!cart.length) { toast.error("Your cart is empty."); return; }
    if (delivery === "ship" && !shipState) { toast.error("Please select your shipping state."); return; }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/uniform/order`, {
        items: cart.map((i) => ({ brand: i.brand, color: i.color, size: i.size, qty: i.qty })),
        promo_code: promo || null,
        delivery_method: delivery,
        ship_state: delivery === "ship" ? shipState : null,
        origin_url: window.location.origin,
      }, authHeader());
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.success("Order placed — pick up your free uniforms on site!");
        setCart([]); setPromo(""); setCartOpen(false); loadMe();
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Checkout failed.");
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="bg-[#0b0e17] text-white min-h-screen">
      {/* Top bar */}
      <header className="fixed top-0 inset-x-0 z-40 bg-[#0b0e17]/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3"><img src={CONTACT.logo} alt="Elite" className="h-8 w-auto bg-white rounded-sm p-1" /></Link>
          <div className="flex items-center gap-3">
            {me && <span className="hidden sm:flex items-center gap-1.5 text-xs font-bold text-[#8fe388] bg-[#8fe388]/10 px-3 py-1.5 rounded-sm"><Gift size={13} /> {freeRemaining} free left</span>}
            <button onClick={() => setCartOpen(true)} data-testid="open-cart" className="relative inline-flex items-center gap-2 text-sm font-bold text-white border border-white/20 rounded-sm px-4 py-1.5 hover:border-white transition-colors">
              <ShoppingBag size={16} /> Cart
              {totalQty > 0 && <span className="absolute -top-2 -right-2 bg-[#e0574d] text-white text-xs w-5 h-5 flex items-center justify-center rounded-full" data-testid="cart-count">{totalQty}</span>}
            </button>
            <button onClick={onLogout} data-testid="portal-logout" className="text-white/50 hover:text-white"><LogOut size={18} /></button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section ref={heroRef} className="relative pt-32 pb-16 min-h-[85vh] flex items-center overflow-hidden">
        <motion.div style={{ y: imgY }} className="hidden md:block absolute right-0 top-1/4 w-[40%] max-w-lg opacity-20 pointer-events-none">
          {products[1]?.image && <img src={products[1].image} alt="" className="w-full" />}
        </motion.div>
        <div className="relative max-w-7xl mx-auto px-6 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-xs font-bold uppercase tracking-[0.3em] text-[#e0574d] mb-6">
            Elite Uniform Store · Vintage White
          </motion.p>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-extrabold font-display leading-[0.92] tracking-tight">
            {["Wear the", "brand.", "Own the", "job."].map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }} animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                >
                  {i === 1 || i === 3 ? <span className="text-[#e0574d]">{line}</span> : line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-8 max-w-md text-white/60 text-lg">
            Your first <span className="text-white font-bold">two pieces are on us.</span> Order official CityWide & Elite Commercial apparel below.
          </motion.p>
          <motion.a href="#shop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-white group">
            Browse apparel <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </motion.a>
        </div>
      </section>

      <Marquee />

      {/* Manifesto */}
      <section className="max-w-7xl mx-auto px-6 py-24 grid sm:grid-cols-3 gap-10">
        {[
          { n: "01", t: "Two Free Pieces", d: "Every team member's first two uniform pieces are complimentary — automatic, no strings." },
          { n: "02", t: "Two Brands", d: "CityWide subcontractor apparel and Elite Commercial Cleaning Service crew wear." },
          { n: "03", t: "Vintage White", d: "A clean, professional look that represents the standard we bring to every facility." },
        ].map((c, i) => (
          <motion.div key={c.n} variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <span className="text-sm font-bold text-[#e0574d] font-display">{c.n}</span>
            <h3 className="mt-2 text-2xl font-bold font-display">{c.t}</h3>
            <p className="mt-3 text-white/50 leading-relaxed">{c.d}</p>
          </motion.div>
        ))}
      </section>

      {/* Products */}
      <section id="shop" className="max-w-7xl mx-auto px-6 pb-32">
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-4xl sm:text-5xl font-extrabold font-display mb-14">
          The Collection
        </motion.h2>
        <div className="grid md:grid-cols-2 gap-8">
          {products.map((p, i) => (
            <motion.div key={p.id} variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
              data-testid={`product-${p.id}`}
              className="group border border-white/10 rounded-sm overflow-hidden bg-gradient-to-b from-white/[0.04] to-transparent hover:border-white/25 transition-colors"
            >
              <div className="relative aspect-square bg-[#11151f] overflow-hidden flex items-center justify-center">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(224,87,77,0.15),transparent_60%)]" />
                <img src={p.image} alt={p.brand} className="relative w-[78%] object-contain transition-transform duration-700 group-hover:scale-105" />
                {p.label && <span className="absolute top-4 left-4 text-[11px] font-bold uppercase tracking-widest bg-white/10 backdrop-blur px-3 py-1 rounded-sm">{p.label}</span>}
              </div>
              <div className="p-7">
                <h3 className="text-2xl font-bold font-display">{p.brand}</h3>
                <p className="mt-2 text-sm text-white/50 leading-relaxed min-h-[2.5rem]">{p.blurb}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-lg font-bold">${p.price.toFixed(2)} <span className="text-xs text-white/40 font-normal">· {p.color}</span></span>
                </div>
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Select size</p>
                  <div className="flex flex-wrap gap-2">
                    {(p.sizes || []).map((s) => (
                      <button key={s} data-testid={`size-${p.id}-${s}`} onClick={() => setSizes((z) => ({ ...z, [p.id]: s }))}
                        className={`w-11 h-10 text-sm font-bold rounded-sm border transition-colors ${sizes[p.id] === s ? "bg-white text-[#0b0e17] border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <button onClick={() => addToCart(p)} data-testid={`add-${p.id}`} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#e0574d] text-white font-bold py-3.5 rounded-sm hover:bg-[#c8483f] transition-colors">
                  <Plus size={18} /> Add to Cart
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Cart drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/60" onClick={() => setCartOpen(false)} />
          <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.4 }}
            className="relative w-full max-w-md bg-[#11151f] border-l border-white/10 h-full flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h3 className="text-xl font-bold font-display">Your Cart</h3>
              <button onClick={() => setCartOpen(false)} className="text-white/50 hover:text-white"><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {cart.length === 0 ? <p className="text-white/40 text-center py-10">Your cart is empty.</p> : cart.map((it, i) => (
                <div key={i} className="flex gap-4 items-center border border-white/10 rounded-sm p-3">
                  <img src={it.image} alt="" className="w-14 h-14 object-contain bg-white/5 rounded-sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate">{it.brand}</p>
                    <p className="text-xs text-white/40">{it.color} · {it.size} · ${it.price}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i, -1)} className="w-7 h-7 flex items-center justify-center border border-white/20 rounded-sm"><Minus size={14} /></button>
                    <span className="w-5 text-center text-sm font-bold">{it.qty}</span>
                    <button onClick={() => setQty(i, 1)} className="w-7 h-7 flex items-center justify-center border border-white/20 rounded-sm"><Plus size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-white/10 space-y-4">
              {/* Delivery method */}
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Delivery</p>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setDelivery("pickup")} data-testid="delivery-pickup"
                    className={`text-sm font-bold py-2.5 rounded-sm border transition-colors ${delivery === "pickup" ? "bg-white text-[#0b0e17] border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                    Pick up on site
                  </button>
                  <button onClick={() => setDelivery("ship")} data-testid="delivery-ship"
                    className={`text-sm font-bold py-2.5 rounded-sm border transition-colors ${delivery === "ship" ? "bg-white text-[#0b0e17] border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}>
                    Ship to me
                  </button>
                </div>
                {delivery === "ship" && (
                  <div className="mt-3">
                    <select data-testid="ship-state" value={shipState} onChange={(e) => setShipState(e.target.value)}
                      className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white outline-none focus:border-[#e0574d]">
                      <option value="" className="text-black">Select shipping state…</option>
                      {US_STATES.map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                    </select>
                    <p className="mt-2 text-xs text-white/40">You'll enter your full shipping address securely at checkout. Standard shipping applies.</p>
                  </div>
                )}
                {delivery === "pickup" && (
                  <p className="mt-2 text-xs text-white/40">Free — pick up your uniforms at the office.</p>
                )}
              </div>

              {/* Promo */}
              <div className="flex items-center gap-2">
                <Tag size={16} className="text-white/40" />
                <input data-testid="promo-input" value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code (optional)" className="flex-1 bg-white/5 border border-white/15 rounded-sm px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-[#e0574d]" />
              </div>

              {/* Summary */}
              <div className="text-sm space-y-1.5 border-t border-white/10 pt-4">
                <div className="flex items-center gap-2 text-white/60">
                  <Gift size={14} className="text-[#8fe388]" /> {freeRemaining} free {freeRemaining === 1 ? "piece" : "pieces"} applied automatically.
                </div>
                <div className="flex justify-between text-white/70" data-testid="summary-items">
                  <span>Paid pieces ({payableQty} × ${shirtPrice})</span>
                  <span>${itemsCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-white/70" data-testid="summary-shipping">
                  <span>Shipping{delivery === "ship" && shipState ? ` (${shipState})` : ""}</span>
                  <span>{delivery === "ship" ? (shippingFee != null ? `$${shippingFee.toFixed(2)}` : "—") : "Free"}</span>
                </div>
                <div className="flex justify-between font-bold text-white pt-1" data-testid="summary-total">
                  <span>Total due</span>
                  <span>${orderTotal.toFixed(2)}</span>
                </div>
              </div>

              <button onClick={checkout} disabled={placing || !cart.length || (delivery === "ship" && !shipState)} data-testid="checkout-btn" className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#0b0e17] font-bold py-4 rounded-sm hover:bg-white/90 transition-colors disabled:opacity-50">
                {placing ? <Loader2 className="animate-spin" size={18} /> : <Check size={18} />}
                {placing ? "Processing..." : orderTotal > 0 ? "Checkout" : "Place Order"}
              </button>
              <p className="text-center text-xs text-white/30">Paid pieces are ${shirtPrice} each. Shipping (if selected) is standard rate by destination.</p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}

export default function Portal() {
  useLenis();
  const [authed, setAuthed] = useState(!!localStorage.getItem(PORTAL_TOKEN));

  useEffect(() => {
    document.title = "Employee Uniform Portal | Elite Facility Solutions";
    return () => { document.title = "Elite Facility Solutions | Commercial Cleaning & Janitorial Services"; };
  }, []);

  const logout = () => { localStorage.removeItem(PORTAL_TOKEN); setAuthed(false); };

  return authed ? <Shop onLogout={logout} /> : <AuthGate onAuthed={() => setAuthed(true)} />;
}
